import {
  getMessageFromNetworkError,
  useMoveZapsToFolder,
} from "@zapier/client";
import { FOLDER_TRASH_ID, getFolderUrl, useNotification } from "@zapier/utils";
import { Link, Text } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";

interface Input {
  currentFolderId: string | undefined;
  assetsAreShared: boolean;
  isAssetOwnedByUser: boolean;
  assetIds: string[] | undefined | null;
  assetTitle: string | undefined | null;
  onSettled?: () => void;
  isBulk?: boolean;
}

export function useMoveZapActions({
  currentFolderId,
  assetsAreShared,
  isAssetOwnedByUser,
  assetIds,
  assetTitle,
  onSettled,
  isBulk = false,
}: Input) {
  const t = useTranslations("move-zap-actions");
  const { createNotification } = useNotification();
  const { moveZapsToFolder } = useMoveZapsToFolder();

  // Don't allow undo Zap
  // 1. From trash
  // 2. If the zap is not owned by the user and user is moving the zap FROM a personal folder
  // 3. The Zap is not in a shared folder
  const showUndoMoveZapAction =
    currentFolderId !== FOLDER_TRASH_ID &&
    (assetsAreShared || isAssetOwnedByUser);

  // Move Zaps between folders
  const moveZapsToFolderWithToasts = ({
    folderId,
    zapIds,
    selectedFolderId,
    selectedFolderName,
    onUndo,
  }) => {
    moveZapsToFolder(
      {
        folderId: selectedFolderId,
        zapIds,
      },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t.rich("notifications.move-success", {
                  assetTitle: assetTitle || "",
                  isBulk: String(isBulk),
                  showUndo: String(showUndoMoveZapAction),
                  selectedFolderName,
                  folderLink: (chunks) => (
                    <Link href={getFolderUrl(selectedFolderId)}>{chunks}</Link>
                  ),
                  undoLink: (chunks) => (
                    <Link
                      onPress={() => {
                        onUndo({
                          selectedFolderId: folderId,
                        });
                      }}
                    >
                      {chunks}
                    </Link>
                  ),
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("errors.unable-to-move", { assetTitle: assetTitle || "" }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
        onSettled: () => {
          onSettled?.();
        },
      },
    );
  };

  // Undo moving Zaps between folders
  const undoMoveZapsToFolderWithToasts = ({ selectedFolderId }) => {
    moveZapsToFolder(
      {
        folderId: selectedFolderId,
        zapIds: assetIds || [],
      },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.restore-success", {
                  assetTitle: assetTitle || "",
                  isBulk: String(isBulk),
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("errors.unable-to-undo", { assetTitle: assetTitle || "" }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
        onSettled: () => {
          onSettled?.();
        },
      },
    );

    return;
  };

  return {
    moveZapsToFolderWithToasts,
    undoMoveZapsToFolderWithToasts,
  };
}
